import { forwardRef, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { AlarmClock, ChevronDown, FileText, Flag, FlaskConical, Inbox, Layers, MailWarning, MessageSquareReply, Paperclip, Plug, Search, Send, Tag, UserPlus, UserRoundCheck, X } from 'lucide-react';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import type { Mailbox, MailCategory, ThreadSummary } from '@mailpilot/shared';
import type { ThreadFilter } from '../../hooks/useThreads';
import { useInboxUnreadByMailbox } from '../../hooks/useMailboxes';
import { dateGroup } from '../../lib/format';
import type { MailView } from '../../pages/MailPage';
import { ThreadRow, type QuickAction } from './ThreadRow';
import { CategoryPicker } from './Category';
import { Skeleton } from '../ui/Feedback';
import { EmptyState, ErrorState } from '../ui/Feedback';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Feedback';
import { spring } from '../ui/motion';

export interface ThreadListProps {
  view: MailView;
  title: string;
  threads: ThreadSummary[];
  selectedId: string | undefined;
  onOpen: (id: string) => void;
  onQuick: (a: QuickAction) => void;
  mailboxes: Map<string, Mailbox>;
  mailboxList: Mailbox[];
  accountId: string | null;
  onAccount: (id: string | null) => void;
  search: string;
  onSearch: (q: string) => void;
  filter: ThreadFilter | null;
  onFilter: (f: ThreadFilter | null) => void;
  categories: Map<string, MailCategory>;
  /** Category key filter ('uncategorized' for none). */
  category: string | null;
  onCategory: (key: string | null) => void;
  compact: boolean;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  noMailboxes: boolean;
  demoMode?: boolean;
  emptyText: string;
}

const VIEWS: { view: MailView; label: string; icon: ReactNode }[] = [
  { view: 'inbox', label: 'Inbox', icon: <Inbox className="h-4 w-4" /> },
  { view: 'needs-reply', label: 'Needs reply', icon: <MessageSquareReply className="h-4 w-4" /> },
  { view: 'flagged', label: 'Flagged', icon: <Flag className="h-4 w-4" /> },
  { view: 'drafts', label: 'Drafts', icon: <FileText className="h-4 w-4" /> },
  { view: 'sent', label: 'Sent', icon: <Send className="h-4 w-4" /> },
];

const FILTERS: { value: ThreadFilter | null; label: string; icon: ReactNode }[] = [
  { value: null, label: 'All', icon: <Layers className="h-3.5 w-3.5" /> },
  { value: 'unread', label: 'Unread', icon: <MailWarning className="h-3.5 w-3.5" /> },
  { value: 'attachments', label: 'Files', icon: <Paperclip className="h-3.5 w-3.5" /> },
  { value: 'followups', label: 'Follow-ups', icon: <AlarmClock className="h-3.5 w-3.5" /> },
];
/** Organizations also filter by owner. */
const TEAM_FILTERS: typeof FILTERS = [
  { value: 'mine', label: 'Mine', icon: <UserRoundCheck className="h-3.5 w-3.5" /> },
  { value: 'unassigned', label: 'Unassigned', icon: <UserPlus className="h-3.5 w-3.5" /> },
];

/** Segmented view switcher (Inbox · Needs reply · Flagged · Drafts · Sent). */
function ViewTabs({ current, unread }: { current: MailView; unread: number }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  return (
    <div role="tablist" aria-label="Mail views" className="flex gap-0.5 rounded-xl bg-sunken p-1">
      {VIEWS.map((v) => {
        const active = current === v.view;
        return (
          <button
            key={v.view}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={v.label}
            data-tip={v.label}
            onClick={() => navigate(`/${v.view}${search}`)}
            className={clsx('tip relative flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors', active ? 'text-ink' : 'text-muted hover:text-ink')}
          >
            {active && <motion.span layoutId="mail-view-tab" transition={spring} className="absolute inset-0 rounded-lg bg-surface shadow-sm ring-1 ring-line/70" />}
            <span className="relative flex items-center gap-1.5">
              {v.icon}
              {active && <span className="hidden min-[440px]:inline">{v.label}</span>}
              {v.view === 'inbox' && unread > 0 && <span className="relative rounded-full bg-primary px-1.5 text-[0.625rem] font-bold leading-4 text-white">{unread > 99 ? '99+' : unread}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Account strip: one chip per mailbox (+ All). Hidden when there's only one account. */
function AccountStrip({ list, active, onPick }: { list: Mailbox[]; active: string | null; onPick: (id: string | null) => void }) {
  const unreadBy = useInboxUnreadByMailbox();
  if (list.length < 2) return null;
  return (
    // Small screens only: on larger ones the account switcher sits in the top bar.
    <div className="-mx-1 flex gap-1 overflow-x-auto px-1 scroll-hide sm:hidden" role="radiogroup" aria-label="Account">
      <button type="button" role="radio" aria-checked={!active} onClick={() => onPick(null)} className="mp-chip shrink-0">
        <Layers className="h-3.5 w-3.5" /> All
      </button>
      {list.map((m) => {
        const n = unreadBy.get(m.id) ?? 0;
        const warn = m.status === 'needs_reauth' || m.status === 'error';
        return (
          <button key={m.id} type="button" role="radio" aria-checked={active === m.id} onClick={() => onPick(m.id)} title={m.email} className="mp-chip max-w-[13rem] shrink-0">
            <span className={clsx('h-2 w-2 shrink-0 rounded-full', warn && 'ring-2 ring-warn')} style={{ background: m.color }} aria-hidden />
            <span className="truncate">{m.displayName}</span>
            {n > 0 && <span className="rounded-full bg-primary/15 px-1.5 text-[0.625rem] font-bold tabular-nums leading-4 text-primary-ink">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const ThreadList = forwardRef<HTMLInputElement, ThreadListProps>(function ThreadList(p, searchRef) {
  const virt = useRef<VirtuosoHandle>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  // stagger only the first successful render, never on refetch
  const animatedOnce = useRef(false);
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => {
    if (!animatedOnce.current && p.threads.length > 0) {
      animatedOnce.current = true;
      setAnimateIn(true);
      const t = setTimeout(() => setAnimateIn(false), 900);
      return () => clearTimeout(t);
    }
  }, [p.threads.length]);

  const selectedIndex = useMemo(() => p.threads.findIndex((t) => t.id === p.selectedId), [p.threads, p.selectedId]);
  useEffect(() => {
    if (selectedIndex >= 0) {
      setFocusIndex(selectedIndex);
      virt.current?.scrollIntoView({ index: selectedIndex, behavior: 'auto' });
    }
  }, [selectedIndex]);

  // Date section headings: computed once per list so each row knows whether it starts a group.
  const groups = useMemo(
    () =>
      p.threads.map((t, i) => {
        const g = dateGroup(t.lastMessageAt);
        return i === 0 || dateGroup(p.threads[i - 1].lastMessageAt) !== g ? g : null;
      }),
    [p.threads],
  );
  const unreadHere = useMemo(() => p.threads.filter((t) => t.unread).length, [p.threads]);
  const { isOrg } = useCurrentWorkspace();
  const inboxUnreadAll = useInboxUnreadByMailbox();
  const inboxUnread = p.accountId ? inboxUnreadAll.get(p.accountId) ?? 0 : [...inboxUnreadAll.values()].reduce((s, n) => s + n, 0);
  const showAccount = !p.accountId && p.mailboxList.length > 1;
  const account = p.accountId ? p.mailboxes.get(p.accountId) ?? null : null;

  // roving focus with arrow keys inside the list
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const n = p.threads.length;
    if (!n) return;
    let i = focusIndex;
    if (e.key === 'ArrowDown') i = Math.min(n - 1, i + 1);
    if (e.key === 'ArrowUp') i = Math.max(0, i - 1);
    if (e.key === 'Home') i = 0;
    if (e.key === 'End') i = n - 1;
    setFocusIndex(i);
    virt.current?.scrollIntoView({
      index: i,
      behavior: 'auto',
      done: () => {
        const id = p.threads[i]?.id;
        if (id) (document.querySelector(`[data-thread-id="${id}"]`) as HTMLElement | null)?.focus();
      },
    });
  };

  const cat = p.category ? p.categories.get(p.category) : null;

  return (
    <section aria-label={`${p.title} messages`} className="flex h-full min-h-0 flex-col">
      <div className="space-y-3 border-b border-hairline px-3 pb-3 pt-3">
        {p.view !== 'folder' && <ViewTabs current={p.view} unread={inboxUnread} />}
        <div className="flex items-end justify-between gap-2 px-1">
          <div className="min-w-0">
            <h1 className="truncate text-[1.0625rem] font-semibold leading-tight text-ink">
              {p.title}
              {account && <span className="ml-1.5 text-[0.8125rem] font-medium text-muted">· {account.displayName}</span>}
            </h1>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
              {p.threads.length > 0 ? (
                <span>
                  {unreadHere > 0 ? <span className="mp-pill mp-pill-lime mr-1 !h-[18px] !px-1.5 !text-[0.625rem]">{unreadHere} unread</span> : 'All read'} · {p.threads.length}
                  {p.hasMore ? '+' : ''} conversation{p.threads.length === 1 ? '' : 's'}
                </span>
              ) : (
                <span>&nbsp;</span>
              )}
              {p.isLoading && p.threads.length > 0 && <Spinner className="h-3.5 w-3.5" label="Refreshing" />}
            </p>
          </div>
        </div>
        <AccountStrip list={p.mailboxList} active={p.accountId} onPick={p.onAccount} />
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
          <input
            ref={searchRef}
            type="search"
            value={p.search}
            onChange={(e) => p.onSearch(e.target.value)}
            placeholder={`Search ${p.title.toLowerCase()}`}
            aria-label="Search mail"
            className="h-9 w-full rounded-xl border border-transparent bg-sunken/80 pl-9 pr-9 text-[0.8125rem] text-ink transition-[background-color,box-shadow] placeholder:text-subtle hover:bg-sunken focus:border-primary/40 focus:bg-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {p.search ? (
            <button type="button" aria-label="Clear search" onClick={() => p.onSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted hover:bg-raised">
              <X className="h-4 w-4" />
            </button>
          ) : (
            <span className="kbd pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" aria-hidden>/</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <div role="radiogroup" aria-label="Filter conversations" className="-my-1 flex min-w-0 flex-1 gap-0.5 overflow-x-auto py-1 scroll-hide">
            {(isOrg ? [...FILTERS, ...TEAM_FILTERS] : FILTERS).map((f) => (
              <button key={f.label} type="button" role="radio" aria-checked={p.filter === f.value} onClick={() => p.onFilter(f.value)} className="mp-chip shrink-0">
                {f.icon}
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto shrink-0">
            <CategoryPicker
              categories={[...p.categories.values()]}
              value={p.category}
              onChange={p.onCategory}
              clearLabel="All categories"
              align="right"
              label="Filter by category"
              trigger={(t) => (
                <button type="button" {...t} aria-label={cat ? `Category: ${cat.name}` : 'Filter by category'} className={clsx('mp-chip max-w-[9.5rem]', cat && 'is-active')} style={cat ? { background: `${cat.color}22`, borderColor: `${cat.color}66` } : undefined}>
                  {cat ? <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: cat.color }} /> : <Tag className="h-3.5 w-3.5" />}
                  <span className="truncate">{cat ? cat.name : p.category === 'uncategorized' ? 'Uncategorised' : 'Category'}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
                </button>
              )}
            />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1" role="list" onKeyDown={onKeyDown}>
        {p.noMailboxes ? (
          <EmptyState
            icon={<Plug className="h-6 w-6" />}
            title="No email accounts yet"
            description={p.demoMode ? 'Connect Gmail, Outlook or any IMAP account — or try the clearly-labelled demo mailbox.' : 'Connect Gmail, Outlook or any other account to start.'}
            actions={
              <>
                <Link to="/settings/accounts"><Button variant="primary">Connect an account</Button></Link>
                {p.demoMode && <Link to="/settings/accounts#add-account"><Button icon={<FlaskConical className="h-4 w-4" />}>Try demo mailbox</Button></Link>}
              </>
            }
          />
        ) : p.error && p.threads.length === 0 ? (
          <ErrorState error={p.error} onRetry={p.onRetry} title="Couldn't load messages" />
        ) : p.isLoading && p.threads.length === 0 ? (
          <div className="space-y-2 px-4 pt-3" aria-busy="true" aria-label="Loading messages">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-3 rounded-2xl p-3">
                <Skeleton className="h-9 w-9 shrink-0 !rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-4/5" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : p.threads.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title={p.search ? 'No matches' : p.filter || p.category ? 'Nothing matches this filter' : 'Nothing here'}
            description={p.search ? `No conversations match “${p.search}”.` : p.filter === 'unread' ? "You're all caught up — no unread conversations." : p.filter === 'attachments' ? 'No conversations with attachments.' : p.filter === 'followups' ? 'No follow-ups due — nobody has left you waiting.' : p.filter === 'mine' ? 'Nothing is assigned to you.' : p.filter === 'unassigned' ? 'Every conversation here has an owner.' : p.emptyText}
            actions={
              p.filter || p.category ? (
                <Button
                  size="sm"
                  onClick={() => {
                    p.onFilter(null);
                    p.onCategory(null);
                  }}
                >
                  Show all
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Virtuoso
            ref={virt}
            className="scroll-thin"
            data={p.threads}
            computeItemKey={(_, t) => t.id}
            endReached={() => p.hasMore && !p.loadingMore && p.onLoadMore()}
            increaseViewportBy={400}
            itemContent={(i, t) => (
              <div role="listitem">
                {groups[i] && <p className={clsx('px-5 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle', i === 0 ? 'pt-2' : 'pt-3')}>{groups[i]}</p>}
                <ThreadRow
                  thread={t}
                  index={i}
                  animateIn={animateIn}
                  selected={t.id === p.selectedId}
                  mailbox={p.mailboxes.get(t.mailboxId)}
                  showAccount={showAccount}
                  onOpen={p.onOpen}
                  onQuick={p.onQuick}
                  tabIndex={i === focusIndex ? 0 : -1}
                  compact={p.compact}
                  category={t.category ? p.categories.get(t.category) : undefined}
                />
              </div>
            )}
            components={{
              Footer: () =>
                p.loadingMore ? (
                  <div className="flex justify-center py-4"><Spinner label="Loading more" /></div>
                ) : p.hasMore ? null : (
                  <p className="py-5 text-center text-xs text-subtle">End of list</p>
                ),
            }}
          />
        )}
      </div>
    </section>
  );
});
